import Vue from 'vue';
import { namespace } from 'vuex-class';
import Component from 'vue-class-component';
import AccountRoleType from '../../enums/AccountRoleType';
import { UNRESTRICTED, cityInScope, emptyInventoryScope, onlyCityId, scopedCities } from '../../inventoryScope';

const Account = namespace('account');
const Workspace = namespace('workspace');

@Component
class ContentGuard extends Vue {
    @Account.State((state) => state.account)
    $_account;
    @Account.State((state) =>
        state.account && state.account.roles ? state.account.roles.map((role) => role.role) : [],
    )
    $_roles;

    @Workspace.State((state) => state.inventoryPermissions)
    $_inventoryPermissionsState;

    @Workspace.State((state) => state.inventoryScope)
    $_inventoryScopeState;

    get $_isUser() {
        return this.$_roleExists(AccountRoleType.USER);
    }

    get $_isAdmin() {
        return this.$_roleExists(AccountRoleType.ADMIN);
    }

    get $_isExecutor() {
        return this.$_roleExists(AccountRoleType.EXECUTOR);
    }

    get $_isInventoryManager() {
        return this.$_roleExists(AccountRoleType.INVENTORY_MANAGER);
    }

    /**
     * Возможности в разделе «Оборудование».
     *
     * Приходят с бэкенда возможностями, а не ролями: право читать есть и у наблюдателя,
     * а право править в 6.3-B2 даст ещё и городская группа. Любая проверка вида
     * «а менеджер ли это» разойдётся с бэкендом на следующем же шаге, поэтому
     * $_isInventoryManager для решений в разделе больше использовать нельзя.
     *
     * ?? {} и сравнение с true — fail closed: пока справочник не доехал или ответ
     * оказался неожиданной формы, интерфейс ведёт себя как у пользователя без прав.
     */
    get $_inventoryPermissions() {
        return this.$_inventoryPermissionsState ?? {};
    }

    get $_canViewInventory() {
        return this.$_inventoryPermissions.view === true;
    }

    get $_canEditInventory() {
        return this.$_inventoryPermissions.edit === true;
    }

    get $_canExportInventory() {
        return this.$_inventoryPermissions.export === true;
    }

    /**
     * Правка общих для всех городов справочников и шаблонов (7-H1). Не то же, что
     * $_canEditInventory: городская группа правит свой город, но не справочник, который
     * переписал бы значения у оборудования чужих городов.
     */
    get $_canManageInventory() {
        return this.$_inventoryPermissions.manage === true;
    }

    /**
     * Городская область раздела: какие города доступны на просмотр, правку и выгрузку.
     *
     * Разрешение и область отвечают на разные вопросы, и по отдельности ни одного из них
     * не хватает. $_canEditInventory — «есть ли право править вообще»; чтобы решить про
     * конкретную позицию, нужен ещё её город: наблюдатель из городской группы видит все
     * города, а править может только свой.
     *
     * Значение по умолчанию закрытое: пока справочник раздела не доехал, правка недоступна.
     */
    get $_inventoryScope() {
        return this.$_inventoryScopeState ?? emptyInventoryScope();
    }

    get $_inventoryVisibleCityIds() {
        return this.$_inventoryScope.viewCityIds;
    }

    get $_inventoryEditableCityIds() {
        return this.$_inventoryScope.editCityIds;
    }

    get $_inventoryExportableCityIds() {
        return this.$_inventoryScope.exportCityIds;
    }

    /**
     * Обязан ли пользователь выбирать город: у ограниченной области позиция без города
     * недоступна, а значит и создать её нельзя.
     */
    get $_inventoryRequiresCity() {
        return this.$_canEditInventory && this.$_inventoryEditableCityIds !== UNRESTRICTED;
    }

    /**
     * Единственный доступный на правку город — его форма подставляет сама.
     */
    get $_inventoryOnlyEditableCityId() {
        return onlyCityId(this.$_inventoryEditableCityIds);
    }

    /**
     * Можно ли вообще завести позицию: право есть, и есть куда её положить.
     */
    get $_canCreateInventoryItem() {
        const cityIds = this.$_inventoryEditableCityIds;

        return this.$_canEditInventory && (cityIds === UNRESTRICTED || cityIds.length > 0);
    }

    $_canViewInventoryCity(cityId) {
        return this.$_canViewInventory && cityInScope(this.$_inventoryVisibleCityIds, cityId);
    }

    $_canEditInventoryCity(cityId) {
        return this.$_canEditInventory && cityInScope(this.$_inventoryEditableCityIds, cityId);
    }

    $_canExportInventoryCity(cityId) {
        return this.$_canExportInventory && cityInScope(this.$_inventoryExportableCityIds, cityId);
    }

    /**
     * Можно ли править эту позицию.
     *
     * Решение всегда по канонической колонке city_id, а не по тому, что показано в карточке:
     * у старых позиций город виден из legacy-атрибута, но доступ он не даёт — до переноса
     * такие позиции для городского пользователя только на чтение.
     *
     * @param {Object|null} item
     * @returns {boolean}
     */
    $_canEditInventoryItem(item) {
        if (!item) {
            return false;
        }

        return this.$_canEditInventoryCity(item.city_id ?? null);
    }

    /**
     * Города справочника, в которых пользователь вправе заводить и править оборудование.
     *
     * @param {Array} cities
     * @returns {Array}
     */
    $_inventoryEditableCities(cities) {
        return scopedCities(cities, this.$_inventoryEditableCityIds);
    }

    $_roleExists($role) {
        return this.$_roles.some((role) => role === $role);
    }

    $_userRoleExists(account, $role) {
        return account.roles.some((role) => role.role === $role);
    }
}

Vue.mixin(ContentGuard);
